import { getTxnInfo, processTransaction } from '@api/form-verification';
import { TxnState } from '@global/constants/verification';
import { mixpanel, trackingEvents } from '@global/monitoring/mixpanel';
import { store } from '@redux/store';
import { navigateTo } from '@services/navigation';
import { LeftVerifications } from './dialogs/types';

const VERIFICATIONS_ATLEAST_MAP: Record<string, number> = {
  'bgv-degree,bgv-tenth,bgv-twelfth,': 1
};

export const basicDetailsReqd = (txnInfo: { [key: string]: any }) => {
  return (
    (txnInfo?.userInputList ?? [])?.length > 0 ||
    (txnInfo?.requiredVerificationFieldList ?? [])?.length > 0
  );
};

export const isFormRequirementsNotSatisfied = (
  filtered: { [key: string]: boolean },
  verificationsAvailable: string[]
) => {
  const requirementsWithExistingVerifications = Object.keys(VERIFICATIONS_ATLEAST_MAP).reduce(
    (acc: Record<string, number>, key) => {
      const keyAr = key.split(',');
      let found = 0;
      keyAr.forEach((k) => {
        if (verificationsAvailable.includes(k)) {
          found++;
        }
      });
      if (found >= VERIFICATIONS_ATLEAST_MAP[key]) {
        acc[key] = VERIFICATIONS_ATLEAST_MAP[key];
      }
      return acc;
    },
    {}
  );

  const requirementsPassed: Record<string, boolean> = {};
  for (const item of Object.keys(requirementsWithExistingVerifications)) {
    let count = requirementsWithExistingVerifications[item] as number;
    let pass = false;
    for (const k of Object.keys(filtered)) {
      if (filtered[k] && item.includes(k)) {
        count--;
      }
      if (count === 0) {
        pass = true;
        break;
      }
    }
    requirementsPassed[item] = pass;
  }
  const failedGroups = Object.keys(requirementsPassed).reduce(
    (acc: typeof VERIFICATIONS_ATLEAST_MAP, x) => {
      if (requirementsPassed[x] === false) {
        acc[x] = VERIFICATIONS_ATLEAST_MAP[x];
      }
      return acc;
    },
    {}
  );
  if (Object.keys(failedGroups)?.length > 0) {
    return failedGroups;
  } else {
    return false;
  }
};

export const areMinimumVerificationsComplete = (): boolean | Record<string, number> => {
  const { formData } = store.getState();
  const { txnInfo = {} } = formData ?? {};
  const obj = Object.keys(txnInfo?.verificationList)?.reduce(
    (acc: { [key: string]: boolean }, key) => {
      acc[key] = Boolean(
        txnInfo?.verificationList?.[key].proceeded && !txnInfo?.verificationList?.[key].markedAsSkip
      );
      return acc;
    },
    {}
  );

  const minVerifications = import.meta.env.VITE_MIN_VERIFICATIONS;
  return minVerifications
    ? isFormRequirementsNotSatisfied(obj, Object.keys(txnInfo?.verificationList))
    : false;
};

const handleNonSkippableIncompletes = (
  setVerificationsLeft: React.Dispatch<React.SetStateAction<LeftVerifications>>
) => {
  const { formData } = store.getState();
  const { txnInfo = {} } = formData ?? {};
  const verificationList = Object.keys(txnInfo?.verificationList ?? {});
  const nonSkippableIncompletes = verificationList?.filter(
    (key: string) =>
      !txnInfo?.verificationList?.[key].proceeded && !txnInfo?.verificationList?.[key].skipAllowed
  );

  if (nonSkippableIncompletes.length > 0) {
    setVerificationsLeft({
      list: nonSkippableIncompletes,
      skippables: false
    });
    return null;
  }
  return true;
};

const handleProfileAndBasicDetailsCheck = (txnInfo: any, profile: any) => {
  const profileVerified = Boolean(profile?.profileDetails?.name);
  if (!profileVerified) {
    navigateTo('/profile-mode');
    return null;
  }
  const userInputCheck = txnInfo?.state === TxnState.NOT_INITIALIZED && basicDetailsReqd(txnInfo);
  if (userInputCheck) {
    navigateTo('/basic-details');
    return null;
  }
  return true;
};

export const onContinueHandlerForUnpaidForms = async (
  setVerificationsLeft: React.Dispatch<React.SetStateAction<LeftVerifications>>
) => {
  const { profile, formData } = store.getState();
  const { txnInfo = {}, txnId, orgInfo } = formData ?? {};
  const checkResult = handleProfileAndBasicDetailsCheck(txnInfo, profile);
  if (checkResult === null) {
    return null;
  }
  const minVerificationsComplete = areMinimumVerificationsComplete();

  // clearInterval(getFetchProcessInterval());

  if (minVerificationsComplete) {
    setVerificationsLeft({ atleastMapping: minVerificationsComplete });
    return null;
  }

  const result = handleNonSkippableIncompletes(setVerificationsLeft);
  if (result === null) {
    return null;
  }

  const incompletes = txnInfo?.filtered?.remaining;
  if (incompletes.length > 0) {
    const skippableIncompletes = incompletes?.filter(
      (key: string) => txnInfo?.verificationList?.[key].skipAllowed
    );
    if (skippableIncompletes.length > 0) {
      setVerificationsLeft({
        list: skippableIncompletes,
        skippables: true
      });
    } else {
      mixpanel.track(trackingEvents.form.events.select.verificationStarted, {
        txnId,
        orgId: orgInfo?.orgId
      });
      navigateTo('/submitted');
    }
  } else {
    mixpanel.track(trackingEvents.form.events.select.verificationStarted, {
      txnId,
      orgId: orgInfo?.orgId
    });
    navigateTo('/submitted');
  }
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const clearIntervalAndTriggerCancelCtrlr = () => {
  // clearInterval(getFetchProcessInterval());
  // triggerCancellationController();
};

export const onContinueHandlerForPaidForms = async (
  setVerificationsLeft: React.Dispatch<React.SetStateAction<LeftVerifications>>
) => {
  const { formData, profile } = store.getState();
  const { txnInfo = {}, txnId, orgInfo } = formData ?? {};
  const orgId = orgInfo?.orgId;

  const checkResult = handleProfileAndBasicDetailsCheck(txnInfo, profile);
  if (checkResult === null) {
    return null;
  }
  const totalCount = Object.keys(txnInfo?.verificationList)?.length;
  if (txnInfo?.filtered?.done?.length > 0 && totalCount === txnInfo?.filtered?.done?.length) {
    // clearIntervalAndTriggerCancelCtrlr();
    mixpanel.track(trackingEvents.form.events.select.verificationStarted, {
      txnId,
      orgId
    });
    navigateTo('/submitted', { replace: true });
  } else {
    if (txnInfo?.state === TxnState.NOT_INITIALIZED) {
      const responseForStarting = await processTransaction({ orgId, txnId });
      if (responseForStarting?.code !== 0) {
        return null;
      }
    }
    // setOpenStartingLoader(true);
    const res = await getTxnInfo(txnId, orgId);
    if (res?.code !== 0) {
      // setLoading(false);
      // clearIntervalAndTriggerCancelCtrlr();
      return null;
    }

    if (res?.data?.filtered?.remaining?.length > 0) {
      const minVerificationsComplete = areMinimumVerificationsComplete();
      // clearInterval(getFetchProcessInterval());
      if (minVerificationsComplete) {
        setVerificationsLeft({ atleastMapping: minVerificationsComplete });
        // return null;
      } else {
        const incompletes = res?.data?.filtered?.remaining;
        if (incompletes.length > 0) {
          const skippableIncompletes = incompletes?.filter(
            (key: string) => txnInfo?.verificationList?.[key].skipAllowed
          );
          if (skippableIncompletes.length > 0) {
            setVerificationsLeft({
              list: skippableIncompletes,
              skippables: true
            });
          }
        }
        const result = handleNonSkippableIncompletes(setVerificationsLeft);

        if (result === null) {
          return null;
        }
      }
    } else {
      if (res?.data?.state === TxnState.COMPLETED) {
        // clearIntervalAndTriggerCancelCtrlr();
        mixpanel.track(trackingEvents.form.events.select.verificationStarted, {
          txnId,
          orgId
        });
        navigateTo('/submitted', { replace: true });
        return res?.data;
      }
    }
    // setTimeout(async () => {
    //   setOpenStartingLoader(false);
    //   if (form2.remaining?.length > 0) {
    //     const minVerificationsComplete = areMinimumVerificationsComplete();
    //     // clearInterval(getFetchProcessInterval());
    //     if (minVerificationsComplete) {
    //       setErrorDialog(minVerificationsComplete);
    //       return null;
    //     }
    //     const nonSkippableIncompletes = form2.remaining?.filter(
    //       (x) => !form2?.list?.[x]?.existInWallet && !formInfo?.[x].skipAllowed
    //     );

    //     if (nonSkippableIncompletes?.length > 0) {
    //       setOpenIncompleteDialog({
    //         items: nonSkippableIncompletes,
    //         skippables: false
    //       });
    //       return null;
    //     }
    //     const skippableIncompletes = form2.remaining?.filter((x) => formInfo?.[x].skipAllowed);
    //     if (skippableIncompletes.length > 0) {
    //       setOpenIncompleteDialog({
    //         items: skippableIncompletes,
    //         skippables: true
    //       });
    //     }
    //   } else {
    //     const interval = setInterval(() => {
    //       triggerProcessDebounced(txnId);
    //     }, Number(process.env.REACT_APP_TIMEOUT_FOR_POLLING_PROCESS));
    //     sessionStorage.setItem('fetchProcessInterval', interval);
    //   }
    // }, 1000);
  }
};

export const handleProcessTransaction = async (orgId: string, txnId: string) => {
  const processRes = await processTransaction({ orgId, txnId });
  if (processRes?.code === 0) {
    await getTxnInfo(txnId, orgId);
  }
};
